<template>
  <v-dialog
    :value="value"
    :persistent="loading"
    width="400"
    class="LinkSchoolDialog"
    @input="$emit('input', $event)"
  >
    <v-card v-if="activeChangeSchoolLayout" :loading="loading">
      <v-form
        ref="sendRequestForm"
        v-model="validForm"
        :disabled="loading"
        @submit.prevent="sendRequest"
      >
        <v-toolbar v-if="changeSchoolLayout">
          <v-btn
            icon
            @click="changeSchoolLayout = false"
          >
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="pl-2">
            Link account to another school
          </v-toolbar-title>
        </v-toolbar>
        <v-card-title v-else>Link account to the school</v-card-title>

        <v-card-text>
          <p>
            You can send request to link your account to the school using school profile id.
            <span class="font-weight-bold">
              School will be able to edit your personal information once your request will be sent
              and school accept it.
            </span>
            School id allows you to receive assignments and other useful information from school
          </p>
          <v-text-field
            v-model="schoolProfileId"
            :rules="rules.schoolProfileId"
            label="School profile id"
            outlined
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn
            text
            @click="close"
          >
            Close
          </v-btn>
          <v-spacer />
          <v-btn
            :disabled="!validForm"
            :loading="loading"
            type="submit"
            color="blue"
            class="white--text"
          >
            Send request
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
    <v-card v-else :loading="loading">
      <v-card-title v-if="activeChildProfile.requested_school_id">
        School linking request sent
      </v-card-title>
      <v-card-text v-if="activeChildProfile.requested_school_id">
        <p class="ma-0">
          Your request was sent to the school with id
          <b>{{ activeChildProfile.requested_school_id }}</b>
        </p>
      </v-card-text>

      <v-divider />

      <v-card-title v-if="activeChildProfile.school_id">
        Edit your school link
      </v-card-title>
      <v-card-text v-if="activeChildProfile.school_id">
        <p>
          You can change linked school or unlink your account from the school
        </p>
        <v-text-field
          v-model="activeChildProfile.school_id"
          readonly
          label="School id"
          color="black"
          outlined
          hide-details
        />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          text
          @click="close"
        >
          Close
        </v-btn>
        <v-spacer />
        <v-btn
          text
          color="red"
          @click="showUnlinkDialog = true"
        >
          Unlink
        </v-btn>
        <v-btn
          text
          color="blue"
          @click="changeSchoolLayout = true"
        >
          Change school
        </v-btn>
      </v-card-actions>
    </v-card>

    <UnlinkSchoolDialog v-model="showUnlinkDialog" />
  </v-dialog>
</template>

<script>
import { get } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import { profileIdRegex } from '@/helpers/regex';
import { handleReqErrorWithToast } from '@/helpers/error-handlers';

export default {
  name: 'LinkSchoolDialog',
  components: {
    UnlinkSchoolDialog: () => import('./UnlinkSchoolDialog.vue'),
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      changeSchoolLayout: false,
      validForm: false,
      loading: false,
      schoolProfileId: '',
      rules: {
        schoolProfileId: [
          v => !!v || 'Field is required',
          v => v?.length > 4 || 'Min length: 8',
          v => v?.length < 17 || 'Max length: 16',
          v => profileIdRegex.test(v) || 'Invalid profile id format',
        ],
      },
      showUnlinkDialog: false,
    };
  },
  computed: {
    ...mapGetters('children', [
      'activeChildProfile',
    ]),
    activeChangeSchoolLayout() {
      return !(this.activeChildProfile.school_id || this.activeChildProfile.requested_school_id)
        || this.changeSchoolLayout;
    },
  },
  methods: {
    ...mapActions('children', [
      'sendSchoolLinkingRequest',
    ]),
    sendRequest() {
      if (this.loading) return;

      this.loading = true;

      this.sendSchoolLinkingRequest({ schoolProfileId: this.schoolProfileId })
        .then(this.$refs.sendRequestForm.reset)
        .then(() => {
          this.schoolProfileId = '';
          this.changeSchoolLayout = false;
          this.$toast.success('School linking request sent');
        })
        .catch((err) => {
          const defaultMsg = get(err, 'response.data.status') === 404
            ? 'School with specified profile id not found'
            : 'Failed to send school linking request';
          handleReqErrorWithToast(err, defaultMsg);
        })
        .finally(() => (this.loading = false));
    },
    close() {
      this.$emit('input', false);
    },
  },
};
</script>
